import { z } from 'zod';
/**
 * The dashboard's creator onboarding — the two-step modal a new creator meets the first time they
 * open the dashboard on a desktop (`docs/creator-onboarding.md`).
 *
 * Signup asks for an email, a password and a username and nothing else. The real name and the
 * first team moved here, to step 1, because neither is anything a tester or a player signing up
 * needs; step 2 is three questions BoardFlight asks for its own sake. This file is step 2's
 * vocabulary and the request that records it.
 *
 * ## Every answer is optional, and the lists are closed
 *
 * Skipping step 2 is a supported answer, recorded as a row with nothing in it — so "how many
 * skipped" is a count rather than an inference. Each list is the whole set the server accepts; a
 * value that is not on it is a 400, because unlike the Board's event batches (`client-events.ts`)
 * there is exactly one client and it ships with the Worker. The column carries **no CHECK**, the
 * 0007/0026/0028 convention: zod is the check and the admin read is tolerant of a value a later
 * release retires.
 *
 * `other` on the two single-choice questions comes with a line of the person's own words; it is the
 * only free text in step 2 besides the note about what they want to build.
 *
 * Labels live here rather than in the dashboard because the admin console prints the same answers
 * and both must call an option by one name.
 */
/** How they heard about BoardFlight. One answer: the first place, not every place. */
export const HEARD_FROM = [
    'board_community',
    'discord',
    'friend',
    'social',
    'video',
    'search',
    'ai_assistant',
    'game_jam',
    'played_a_game',
    'other',
];
export const HEARD_FROM_LABELS = {
    board_community: "Board's developer community",
    discord: 'A Discord server',
    friend: 'A friend or colleague',
    social: 'Social media',
    video: 'YouTube or a stream',
    search: 'A search engine',
    ai_assistant: 'An AI assistant',
    game_jam: 'A game jam',
    played_a_game: 'I tested a game on it',
    other: 'Somewhere else',
};
/** Who they are. One answer — the one that describes them best. */
export const CREATOR_ROLES = [
    'indie',
    'studio',
    'hobbyist',
    'tabletop',
    'educator',
    'family',
    'playtester',
    'other',
];
export const CREATOR_ROLE_LABELS = {
    indie: 'Indie or solo developer',
    studio: 'Part of a studio',
    hobbyist: 'Hobbyist, making games for fun',
    tabletop: 'Board or tabletop game designer',
    educator: 'Teacher or student',
    family: 'Making something for my family',
    playtester: 'Here to playtest',
    other: 'Something else',
};
/** What they want to build. As many as apply — somebody making a party game for kids is both. */
export const BUILD_KINDS = [
    'party',
    'family',
    'strategy',
    'puzzle',
    'arcade',
    'educational',
    'port',
    'not_a_game',
    'exploring',
];
export const BUILD_KIND_LABELS = {
    party: 'Party games',
    family: 'Games for kids and families',
    strategy: 'Strategy and board games',
    puzzle: 'Puzzles',
    arcade: 'Arcade and action',
    educational: 'Learning games',
    port: 'A port of a game I already have',
    not_a_game: 'Something that isn’t a game',
    exploring: 'Not sure yet, still exploring',
};
/** A line of somebody's own words. Trimmed; blank is the same as absent. */
const Words = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? undefined : value))
    .optional();
/**
 * `POST /v1/me/creator-onboarding` — step 2's answers, or none of them (Skip).
 *
 * Either way it finishes the onboarding: the modal does not come back. Posting again replaces the
 * answers, which is what makes a double-pressed Finish harmless.
 *
 * The `…Other` lines are kept only beside `other` — a stale line left in a field the person then
 * hid by choosing something else is not an answer they gave.
 */
export const CreatorOnboardingRequest = z
    .object({
    heardFrom: z.enum(HEARD_FROM).optional(),
    heardFromOther: Words(120),
    role: z.enum(CREATOR_ROLES).optional(),
    roleOther: Words(120),
    building: z
        .array(z.enum(BUILD_KINDS))
        .max(BUILD_KINDS.length)
        .default([])
        .transform((kinds) => [...new Set(kinds)]),
    buildingNote: Words(500),
})
    .transform((body) => ({
    ...body,
    heardFromOther: body.heardFrom === 'other' ? body.heardFromOther : undefined,
    roleOther: body.role === 'other' ? body.roleOther : undefined,
}));
/* ------------------------------------------------------------------ the console's read
 *
 * Here rather than in `admin.ts` because this is the whole of the feature's vocabulary in one file,
 * and nothing below speaks about plans — the reason `admin.ts` is kept apart. Only the admin
 * console's `GET /v1/admin/creator-onboarding` returns this shape; no public route does.
 */
export const AdminCreatorOnboardingQuery = z.object({
    days: z.coerce.number().int().min(1).max(400).default(30),
});
/** One option and how many accounts gave it. `known: false` is a key a later release retired. */
export const AdminCreatorAnswerCount = z.object({
    key: z.string(),
    label: z.string(),
    known: z.boolean(),
    accounts: z.number().int(),
    /** Of `accounts`, the ones that have since uploaded a build — what the answer led to. */
    shipped: z.number().int(),
});
/** An answer with somebody's own words in it. Named by username only — the admin rule (CLAUDE.md). */
export const AdminCreatorNote = z.object({
    userId: z.string(),
    username: z.string(),
    heardFrom: z.string().nullable(),
    heardFromOther: z.string().nullable(),
    role: z.string().nullable(),
    roleOther: z.string().nullable(),
    building: z.array(z.string()),
    buildingNote: z.string().nullable(),
    createdAt: z.number().int(),
});
export const AdminCreatorOnboardingSummary = z.object({
    from: z.number().int(),
    to: z.number().int(),
    /** Accounts that signed up in the range and have not been through the modal yet. */
    owed: z.number().int(),
    /** Went through step 2 in the range and answered at least one question. */
    answered: z.number().int(),
    /** Went through step 2 in the range and pressed Skip — or Finish with nothing chosen. */
    skipped: z.number().int(),
    heardFrom: z.array(AdminCreatorAnswerCount),
    roles: z.array(AdminCreatorAnswerCount),
    building: z.array(AdminCreatorAnswerCount),
    /** The latest answers that carry free text, newest first. */
    notes: z.array(AdminCreatorNote),
});
//# sourceMappingURL=creator-onboarding.js.map